/**
 * Tenant management contracts — used by the tenants handler.
 *
 * Covers tenant CRUD, settings management, usage statistics, and template
 * discovery. Entities live in `../entities/tenant.ts`; this module defines the
 * request/response envelopes exposed over the HTTP API.
 *
 * Request bodies use camelCase (including the super_admin-only create flow).
 * Persisted `Settings` keys retain the snake_case form already in DynamoDB.
 */
import { z } from 'zod';
import { Email, ISO8601Date, TenantId, TenantSlug } from '../primitives/index.js';
import { PlanTier, TenantStatus, Vertical } from '../enums/index.js';
// ============================================================================
// REQUEST BODIES
// ============================================================================
/**
 * `POST /tenants` — super_admin-only. Creates the tenant, its MySQL schema and
 * the owner Cognito user in a single call.
 */
export const CreateTenantRequestSchema = z.object({
    slug: TenantSlug,
    companyName: z.string().min(1).max(255),
    name: z.string().min(1).max(100),
    email: Email,
    password: z.string().min(8).max(128),
    plan: PlanTier.optional(),
    templateId: Vertical.optional(),
});
/**
 * `PUT /tenants/:id` — all fields optional. Only super_admin may send `plan`
 * or `status`.
 */
export const UpdateTenantRequestSchema = z
    .object({
    name: z.string().min(1).max(255),
    settings: z.record(z.string(), z.unknown()),
    plan: PlanTier,
    status: TenantStatus,
})
    .partial();
/**
 * `PUT /tenants/:id/settings` — updates tenant `Settings` in DynamoDB.
 * The handler whitelists known keys and rejects empty bodies.
 */
export const UpdateTenantSettingsRequestSchema = z.record(z.string(), z.unknown());
// ============================================================================
// RESPONSE BODIES
// ============================================================================
/**
 * Loose shape of the persisted `Settings` blob. Keys use snake_case to mirror
 * the data already in DynamoDB (`slack_webhook_url`, `notification_emails`, …).
 */
export const TenantSettingsBlobSchema = z.record(z.string(), z.unknown());
export const TenantLimitsSchema = z.record(z.string(), z.unknown());
export const TenantUsageBlobSchema = z.record(z.string(), z.unknown());
/** Row shape in `GET /tenants` and `GET /tenants/:id`. */
export const TenantListItemSchema = z.object({
    id: TenantId,
    slug: TenantSlug,
    name: z.string(),
    plan: PlanTier,
    status: TenantStatus,
    settings: TenantSettingsBlobSchema.nullable(),
    limits: TenantLimitsSchema.nullable(),
    usage: TenantUsageBlobSchema.nullable(),
    trialEndsAt: ISO8601Date.nullable(),
    createdAt: ISO8601Date.nullable(),
    updatedAt: ISO8601Date.nullable(),
});
export const ListTenantsResponseSchema = z.object({
    tenants: z.array(TenantListItemSchema),
    count: z.number().int().nonnegative(),
});
/** `GET /tenants/:id` — includes optional billing, visible only to super_admin. */
export const TenantDetailSchema = TenantListItemSchema.extend({
    billing: z.record(z.string(), z.unknown()).nullable().optional(),
});
export const GetTenantResponseSchema = z.object({
    tenant: TenantDetailSchema,
});
/** `POST /tenants` success payload. */
export const CreateTenantResponseSchema = z.object({
    message: z.string().min(1),
    tenantId: TenantId,
    slug: TenantSlug,
    email: Email,
    trialEndsAt: ISO8601Date,
    templateId: Vertical.optional(),
    templateName: z.string().optional(),
});
/** `PUT /tenants/:id` success payload. */
export const UpdateTenantResponseSchema = z.object({
    message: z.string().min(1),
    tenant: z.object({
        id: TenantId,
        slug: TenantSlug,
        name: z.string(),
        plan: PlanTier,
        status: TenantStatus,
        settings: TenantSettingsBlobSchema.nullable(),
        updatedAt: ISO8601Date,
    }),
});
/** `GET /tenants/:id/settings` payload. */
export const TenantSettingsUsageSchema = z.object({
    users: z.number().int().nonnegative(),
    connectors: z.number().int().nonnegative(),
    jobs: z.number().int().nonnegative(),
    ordersThisMonth: z.number().int().nonnegative(),
    storageMb: z.number().int().nonnegative(),
});
export const GetTenantSettingsResponseSchema = z.object({
    settings: TenantSettingsBlobSchema,
    plan: PlanTier,
    limits: TenantLimitsSchema,
    usage: TenantSettingsUsageSchema,
});
/** `PUT /tenants/:id/settings` success payload. */
export const UpdateTenantSettingsResponseSchema = z.object({
    message: z.string().min(1),
    settings: TenantSettingsBlobSchema,
});
/** `GET /tenants/:id/usage` payload. */
export const TenantUsageDataSchema = z.object({
    dailySalesRows: z.number().int().nonnegative(),
    orderRows: z.number().int().nonnegative(),
    products: z.number().int().nonnegative(),
    customers: z.number().int().nonnegative(),
});
export const GetTenantUsageResponseSchema = z.object({
    usage: z.object({
        plan: PlanTier,
        limits: TenantLimitsSchema,
        current: z.object({
            users: z.number().int().nonnegative(),
            connectors: z.number().int().nonnegative(),
            jobs: z.number().int().nonnegative(),
            ordersThisMonth: z.number().int().nonnegative(),
            storageMb: z.number().int().nonnegative(),
        }),
        jobRuns30d: z.record(z.string(), z.number().int().nonnegative()),
        data: TenantUsageDataSchema.nullable(),
    }),
});
// ============================================================================
// TEMPLATES
// ============================================================================
/** Summary row returned by `GET /templates`. */
export const TemplateSummarySchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    description: z.string(),
    version: z.string().min(1),
    expectedTables: z.array(z.string()).readonly(),
});
export const ListTemplatesResponseSchema = z.object({
    templates: z.array(TemplateSummarySchema),
    count: z.number().int().nonnegative(),
});
/** Full template detail returned by `GET /templates/:id`. */
export const TemplateDetailSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    description: z.string(),
    version: z.string().min(1),
    expectedTables: z.array(z.string()).readonly(),
    /** DataModel structure (dimensions/facts/features). Intentionally loose. */
    dataModel: z.record(z.string(), z.unknown()),
});
export const GetTemplateResponseSchema = z.object({
    template: TemplateDetailSchema,
});
//# sourceMappingURL=index.js.map